/**
 * Public Order Tracking page — `/track` and `/track/:orderNumber`.
 *
 * No auth required. Customers enter (or arrive with prefilled) order #
 * + the email used at checkout. The backend `GET /api/store/orders/track`
 * returns a public-safe payload (no Stripe IDs, no internal metadata).
 *
 * Anti-enumeration: order-number-only lookup is not allowed — we always
 * require the customer's email match. A wrong combo returns 404 with a
 * generic message, identical to a missing order.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Package, Truck, Search, ExternalLink, MapPin, Clock4,
  CheckCircle2, CircleDashed, AlertTriangle, ArrowLeft, Mail,
} from 'lucide-react';
import ZheltaLogo from '../components/ZheltaLogo';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

const ORDER_STAGE_PIPELINE = [
  { id: 'pending',    label: 'Order placed' },
  { id: 'paid',       label: 'Payment received' },
  { id: 'processing', label: 'Preparing' },
  { id: 'shipped',    label: 'Shipped' },
  { id: 'delivered',  label: 'Delivered' },
];

const SHIPMENT_STATUS_LABELS = {
  label_created:     'Label created',
  in_transit:        'In transit',
  out_for_delivery:  'Out for delivery',
  delivered:         'Delivered',
  exception:         'Exception',
  returned:          'Returned',
};

function carrierTrackingUrl(carrier, tracking) {
  if (!tracking) return null;
  const c = (carrier || '').toLowerCase();
  if (c.includes('ups'))   return `https://www.ups.com/track?tracknum=${encodeURIComponent(tracking)}`;
  if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tracking)}`;
  if (c.includes('usps'))  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tracking)}`;
  if (c.includes('dhl'))   return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encodeURIComponent(tracking)}`;
  return null;
}

function fmtUsd(amount, currency = 'usd') {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
}

function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function formatAddress(a) {
  if (!a) return null;
  return [a.name, a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(', '), a.country].filter(Boolean);
}

export default function OrderTrackingPage() {
  const { orderNumber: paramOrderNumber } = useParams();
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState(paramOrderNumber || '');
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false);

  // If the URL gave us an order number, prefill it.
  useEffect(() => { if (paramOrderNumber) setOrderNumber(paramOrderNumber.toUpperCase()); }, [paramOrderNumber]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null); setData(null); setSubmittedOnce(true);
    if (!orderNumber.trim() || !email.trim()) {
      setErr('Enter both your order number and the email used at checkout.');
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/api/store/orders/track`, {
        params: { order_number: orderNumber.trim().toUpperCase(), email: email.trim().toLowerCase() },
      });
      setData(res);
      // Reflect the order number in the URL for shareable deep-links.
      if (paramOrderNumber !== res.order_number) {
        navigate(`/track/${encodeURIComponent(res.order_number)}`, { replace: true });
      }
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(detail || "We couldn't find that order. Double-check the order # and email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="order-tracking-page">
      {/* Header */}
      <header className="bg-white border-b border-[#eaeaea]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900 transition-colors duration-150" data-testid="track-home-link">
            <ZheltaLogo className="h-7 w-auto" />
          </Link>
          <Link to="/services" className="text-[12.5px] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Back to services
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-amber-700 mb-2">Track your order</div>
          <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight text-zinc-900">Where's my order?</h1>
          <p className="text-[13.5px] text-zinc-600 mt-2 max-w-xl leading-relaxed">
            Enter the order number from your receipt email and the email address you used at checkout.
            We'll show you exactly where your package is.
          </p>
        </div>

        {/* Lookup form */}
        <form onSubmit={submit} className="rounded-2xl border border-[#eaeaea] bg-white p-5 sm:p-6 space-y-4" data-testid="track-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] font-bold tracking-[0.16em] uppercase text-zinc-500 mb-1.5">Order number</label>
              <div className="relative">
                <Package size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="track-order-number"
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ZH-XXXXXXXX"
                  className="w-full text-[13px] pl-9 pr-3 py-2.5 rounded-md border border-[#eaeaea] focus:border-zinc-900 outline-none font-mono uppercase"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10.5px] font-bold tracking-[0.16em] uppercase text-zinc-500 mb-1.5">Email used at checkout</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="track-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full text-[13px] pl-9 pr-3 py-2.5 rounded-md border border-[#eaeaea] focus:border-zinc-900 outline-none"
                  autoComplete="email"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-zinc-500">
              Lost your order number? Search your inbox for "ZHELTA order".
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="track-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#f5a623] text-[#0e0e10] text-[12.5px] font-semibold hover:bg-[#e09611] disabled:opacity-50 transition-colors duration-150"
            >
              <Search size={13} /> {loading ? 'Looking up…' : 'Track order'}
            </button>
          </div>
          {err && submittedOnce && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[12.5px] text-red-700" data-testid="track-error">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
        </form>

        {data && <OrderResult data={data} />}
      </main>
    </div>
  );
}

function OrderResult({ data }) {
  const currentStageIdx = useMemo(() => {
    const idx = ORDER_STAGE_PIPELINE.findIndex((s) => s.id === data.status);
    if (idx >= 0) return idx;
    if (data.status === 'cancelled') return -1;
    return 0;
  }, [data.status]);
  const cancelled = data.status === 'cancelled';
  const primaryShipment = data.shipment || (data.shipments && data.shipments[0]) || null;
  const trackUrl = primaryShipment ? carrierTrackingUrl(primaryShipment.carrier, primaryShipment.tracking_number) : null;
  const addrLines = formatAddress(data.shipping_address);

  return (
    <div className="space-y-5" data-testid="track-result">
      {/* Hero summary */}
      <div className="rounded-2xl border border-[#eaeaea] bg-white p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-amber-700 mb-1">Order</div>
            <h2 className="text-[20px] font-bold font-mono text-zinc-900" data-testid="track-result-order-number">{data.order_number}</h2>
            <div className="text-[12.5px] text-zinc-500 mt-0.5">Placed {fmtDateTime(data.created_at)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Total</div>
            <div className="text-[18px] font-semibold text-zinc-900">{fmtUsd(data.amount, data.currency)}</div>
          </div>
        </div>

        {/* Pipeline */}
        {!cancelled ? (
          <div className="mt-6 grid grid-cols-5 gap-1.5" data-testid="track-pipeline">
            {ORDER_STAGE_PIPELINE.map((s, i) => {
              const reached = i <= currentStageIdx;
              const active = i === currentStageIdx;
              return (
                <div key={s.id} className="flex flex-col items-start gap-1.5">
                  <div className={`w-full h-1 rounded-full transition-colors duration-200 ${reached ? 'bg-amber-500' : 'bg-zinc-200'}`} />
                  <div className="flex items-center gap-1.5">
                    {reached ? (
                      <CheckCircle2 size={12} className={active ? 'text-amber-600' : 'text-emerald-600'} />
                    ) : (
                      <CircleDashed size={12} className="text-zinc-300" />
                    )}
                    <span className={`text-[10.5px] font-semibold uppercase tracking-wider ${reached ? 'text-zinc-800' : 'text-zinc-400'}`}>
                      {s.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 rounded-md bg-rose-50 border border-rose-200 px-4 py-3 text-[12.5px] text-rose-800 flex items-start gap-2" data-testid="track-cancelled">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            This order was cancelled. If you believe this is an error, contact <a className="underline font-semibold" href="mailto:support@zhelta.com">support@zhelta.com</a>.
          </div>
        )}
      </div>

      {/* Shipment card */}
      {primaryShipment && (
        <div className="rounded-2xl border border-[#eaeaea] bg-white p-5 sm:p-6" data-testid="track-shipment">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
                <Truck size={16} className="text-violet-700" />
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-violet-700">Shipment</div>
                <div className="text-[14px] font-semibold text-zinc-900">
                  {primaryShipment.carrier}{primaryShipment.service ? ` · ${primaryShipment.service}` : ''}
                </div>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-violet-50 text-violet-800 border border-violet-100" data-testid="track-shipment-status">
              {SHIPMENT_STATUS_LABELS[primaryShipment.status] || primaryShipment.status}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-500 mb-1">Tracking number</div>
              <div className="font-mono text-zinc-900" data-testid="track-shipment-number">{primaryShipment.tracking_number}</div>
              {trackUrl && (
                <a href={trackUrl} target="_blank" rel="noopener noreferrer"
                   className="mt-1 inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-700 hover:underline" data-testid="track-carrier-link">
                  <ExternalLink size={11} /> Track on {primaryShipment.carrier}
                </a>
              )}
            </div>
            {addrLines && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-500 mb-1 inline-flex items-center gap-1.5">
                  <MapPin size={11} /> Shipping to
                </div>
                <div className="text-zinc-800 leading-snug">
                  {addrLines.map((l, i) => <div key={i}>{l}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items */}
      {(data.items || []).length > 0 && (
        <div className="rounded-2xl border border-[#eaeaea] bg-white p-5 sm:p-6">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Items</div>
          <div className="divide-y divide-zinc-50">
            {data.items.map((it, i) => (
              <div key={i} className="py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-zinc-900 truncate">{it.description || it.name || '—'}</div>
                  <div className="text-[11px] text-zinc-500">qty {it.quantity || 1}</div>
                </div>
                <div className="font-semibold text-zinc-900 text-[13px]">{fmtUsd(it.amount, data.currency)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {(data.timeline || []).length > 0 && (
        <div className="rounded-2xl border border-[#eaeaea] bg-white p-5 sm:p-6" data-testid="track-timeline">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3 inline-flex items-center gap-1.5">
            <Clock4 size={11} /> Activity
          </div>
          <ol className="space-y-3">
            {data.timeline.slice().reverse().map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-zinc-900 capitalize">{t.status?.replace(/_/g, ' ')}</div>
                  {t.note && <div className="text-[11.5px] text-zinc-500">{t.note}</div>}
                  <div className="text-[10.5px] text-zinc-400 mt-0.5">{fmtDateTime(t.at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="text-center text-[12px] text-zinc-500 pt-2">
        Need help? <a href="mailto:support@zhelta.com" className="text-amber-700 font-semibold hover:underline">support@zhelta.com</a>
      </div>
    </div>
  );
}
